import { gql, useLazyQuery } from '@apollo/client';
import { Link, TextField } from '@mui/material';
import { validateCsvFile } from '@utils/csvFileValidation';
import { safeWindowOpen } from '@utils/urlSanitization';
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Typography,
  useSnackbar,
  useTheme,
} from 'hephaestus/web';
import {
  AttachFile as AttachFileIcon,
  Cancel as CancelIcon,
  ContentPaste as ContentPasteIcon,
  Download as DownloadIcon,
  InfoOutlined as InfoOutlinedIcon,
} from 'hephaestus/webIcons';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '../dialog/dialog';
import { DroppableFileInput } from '../droppableFileInput/droppableFileInput';
import { FileTypes } from '../droppableFileInput/fileTypes';

const ACCEPTED_TYPES: FileTypes[] = [FileTypes.CSV];
const COMPLETE_PROGRESS = 100;

const CSV_IMPORT_ERRORS = {
  EMPTY_PASTE: 'bulkImport:errors.emptyPaste',
  INVALID_CONTENT: 'bulkImport:errors.invalidContent',
  PROCESS_FAILED: 'bulkImport:errors.processFailed',
  UNKNOWN_ERROR: 'bulkImport:errors.unknownError',
} as const;

const FETCH_TEMPLATE_DOWNLOAD_URL = gql`
  query fetchImportFileTemplate($importFileType: ImportFileTypes!) {
    fetchImportFileTemplate(importFileType: $importFileType) {
      downloadUrl
    }
  }
`;

export type ImportCsvModalProps = {
  open: boolean;
  onClose: () => void;
  onImport: (
    file: File,
    onProgress?: (progress: number, message?: string) => void,
  ) => Promise<void>;
  importFileType: string;
  title?: string;
  description?: string;
  importButtonLabel?: string;
  successMessage?: string;
  errorMessage?: string;
  testIdPrefix?: string;
};

export const ImportCsvModal = ({
  open,
  onClose,
  onImport,
  importFileType,
  title,
  description,
  importButtonLabel,
  successMessage,
  errorMessage,
  testIdPrefix,
}: ImportCsvModalProps) => {
  const { t } = useTranslation(['shared', 'components', 'bulkImport']);
  const theme = useTheme();
  const [file, setFile] = useState<File | undefined>();
  const [uploadPending, setUploadPending] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showPasteDialog, setShowPasteDialog] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [fetchTemplateDownloadUrl] = useLazyQuery(FETCH_TEMPLATE_DOWNLOAD_URL, {
    fetchPolicy: 'no-cache',
  });

  const handleClose = useCallback(() => {
    if (!uploadPending) {
      setFile(undefined);
      setUploadProgress(0);
      onClose();
    }
  }, [uploadPending, onClose]);

  const downloadTemplate = useCallback(async () => {
    const queryResult = await fetchTemplateDownloadUrl({
      variables: { importFileType },
    });
    const templateUrl = queryResult?.data?.fetchImportFileTemplate?.downloadUrl;
    if (!templateUrl || queryResult?.error) {
      enqueueSnackbar(
        t('bulkImport:errorFetchingTemplate', {
          error: queryResult?.error,
        }),
        { variant: 'error' },
      );
      return;
    }
    if (window && templateUrl) {
      setTimeout(() =>
        safeWindowOpen(templateUrl, {
          target: '_blank',
          onError: (errorMsg) => {
            enqueueSnackbar(errorMsg, { variant: 'error' });
          },
        }),
      );
    }
  }, [fetchTemplateDownloadUrl, importFileType, enqueueSnackbar, t]);

  const removeFile = useCallback(() => {
    setFile(undefined);
    setUploadProgress(0);
  }, []);

  const handleProgress = useCallback((progress: number, _message?: string) => {
    setUploadProgress(Math.min(progress, COMPLETE_PROGRESS));
  }, []);

  const handleSubmitWithFile = useCallback(
    async (fileToImport: File) => {
      try {
        setUploadPending(true);
        setUploadProgress(0);

        await onImport(fileToImport, handleProgress);

        setUploadProgress(COMPLETE_PROGRESS);

        enqueueSnackbar(
          successMessage || `${fileToImport.name} imported successfully`,
          { variant: 'success' },
        );
        setFile(undefined);
        setUploadProgress(0);
        onClose();
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        enqueueSnackbar(
          errorMessage
            ? `${errorMessage}: ${errorMsg}`
            : `Import failed: ${errorMsg}`,
          { variant: 'error' },
        );
        setUploadProgress(0);
      } finally {
        setUploadPending(false);
      }
    },
    [
      onImport,
      handleProgress,
      successMessage,
      enqueueSnackbar,
      onClose,
      errorMessage,
    ],
  );

  const handlePasteSubmit = useCallback(async () => {
    if (!pastedText.trim()) {
      enqueueSnackbar(
        t(CSV_IMPORT_ERRORS.EMPTY_PASTE, {
          defaultValue: 'Please paste CSV content',
        }),
        { variant: 'error' },
      );
      return;
    }

    try {
      const blob = new Blob([pastedText], { type: 'text/csv' });
      const csvFile = new File([blob], 'pasted-data.csv', { type: 'text/csv' });

      const validationResult = await validateCsvFile(csvFile);
      if (!validationResult.valid) {
        enqueueSnackbar(
          t(CSV_IMPORT_ERRORS.INVALID_CONTENT, {
            defaultValue: 'Invalid CSV content: {{error}}',
            error:
              validationResult.error ||
              t(CSV_IMPORT_ERRORS.UNKNOWN_ERROR, {
                defaultValue: 'Unknown error',
              }),
          }),
          { variant: 'error' },
        );
        return;
      }

      setShowPasteDialog(false);
      setPastedText('');
      setFile(csvFile);

      await handleSubmitWithFile(csvFile);
    } catch {
      enqueueSnackbar(
        t(CSV_IMPORT_ERRORS.PROCESS_FAILED, {
          defaultValue: 'Failed to process pasted content',
        }),
        { variant: 'error' },
      );
    }
  }, [pastedText, enqueueSnackbar, t, handleSubmitWithFile]);

  const handleSubmit = useCallback(async () => {
    if (!file) return;
    await handleSubmitWithFile(file);
  }, [file, handleSubmitWithFile]);

  const handleFileChosen = useCallback((dropFile: File) => {
    setFile(dropFile);
  }, []);

  const openPasteDialog = useCallback(() => {
    setShowPasteDialog(true);
  }, []);

  const closePasteDialog = useCallback(() => {
    setShowPasteDialog(false);
    setPastedText('');
  }, []);

  const handlePastedTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPastedText(e.target.value);
    },
    [],
  );

  const prefix = testIdPrefix ? `${testIdPrefix}-` : '';
  const modalTestId = `${prefix}import-csv-modal`;
  const cancelButtonTestId = `${prefix}import-csv-cancel-button`;
  const submitButtonTestId = `${prefix}import-csv-submit-button`;
  const dropZoneTestId = `${prefix}import-csv-drop-zone`;
  const copyPasteTestId = `${prefix}import-csv-copy-paste-button`;
  const removeFileTestId = `${prefix}import-csv-remove-file-button`;
  const downloadTemplateTestId = `${prefix}import-csv-download-template-link`;
  const progressBarTestId = `${prefix}import-csv-progress-bar`;
  const pasteDialogTestId = `${prefix}paste-csv-dialog`;
  const pasteDialogTextareaTestId = `${prefix}paste-csv-textarea`;
  const pasteDialogDownloadTemplateTestId = `${prefix}paste-csv-download-template-link`;
  const pasteDialogCancelTestId = `${prefix}paste-csv-cancel-button`;
  const pasteDialogImportTestId = `${prefix}paste-csv-import-button`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      data-testid={modalTestId}
    >
      <DialogTitle onClose={handleClose}>
        {title || t('shared:importCsv')}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {description && !file && !uploadPending && (
            <Typography variant='bodyLarge' sx={{ fontSize: '16px' }}>
              {description}
            </Typography>
          )}

          {!file && !uploadPending && (
            <Box
              sx={{
                mb: 1,
                p: 2,
                backgroundColor: theme.color.surfaceContainerVariant,
                borderRadius: theme.borderRadius.md,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
              }}
            >
              <InfoOutlinedIcon
                fontSize='small'
                sx={{
                  color: theme.color.onSurfaceVariant,
                }}
              />
              <Typography
                variant='bodyMedium'
                component='span'
                sx={{ color: theme.color.onSurface, fontWeight: 500 }}
              >
                {t('bulkImport:notSureHowToFormat')}{' '}
                <Link
                  onClick={downloadTemplate}
                  sx={{ cursor: 'pointer' }}
                  data-testid={downloadTemplateTestId}
                >
                  {t('bulkImport:template')}
                </Link>
              </Typography>
            </Box>
          )}

          {/* File upload area or file display */}
          {!file && !uploadPending && (
            <Box sx={{ marginBottom: '16px' }}>
              <DroppableFileInput
                acceptedTypes={ACCEPTED_TYPES}
                onFileChosen={handleFileChosen}
                testId={dropZoneTestId}
              />
            </Box>
          )}

          {/* File attached view */}
          {file && !uploadPending && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1.5,
                border: `1px solid ${theme.color.outline}`,
                borderRadius: theme.borderRadius.md,
                backgroundColor: theme.color.surface,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <AttachFileIcon
                  sx={{
                    color: theme.color.onSurfaceVariant,
                    fontSize: '18px',
                  }}
                />
                <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                  {file.name}
                </Typography>
              </Box>
              <IconButton
                aria-label='close'
                size='xsmall'
                onClick={removeFile}
                data-testid={removeFileTestId}
              >
                <CancelIcon
                  sx={{
                    fontSize: '20px',
                    color: theme.color.onSurfaceVariant,
                  }}
                  fontSize='inherit'
                />
              </IconButton>
            </Box>
          )}

          {/* Loading progress */}
          {uploadPending && file && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography
                    variant='bodyMedium'
                    sx={{ color: 'text.secondary', fontWeight: 500 }}
                  >
                    Importing... {file.name}
                  </Typography>
                </Box>
                <Typography
                  variant='bodyMedium'
                  sx={{
                    fontWeight: 'medium',
                  }}
                >
                  {uploadProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant='determinate'
                value={uploadProgress}
                aria-label={`Import progress: ${uploadProgress}%`}
                aria-valuenow={uploadProgress}
                aria-valuemin={0}
                aria-valuemax={100}
                data-testid={progressBarTestId}
              />
            </Box>
          )}

          {/* Copy and paste securities */}
          {!uploadPending && !file && (
            <Box
              onClick={openPasteDialog}
              data-testid={copyPasteTestId}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                cursor: 'pointer',
                color: theme.color.onSurface,
              }}
            >
              <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                {t('bulkImport:copyAndPaste')}
              </Typography>
              <ContentPasteIcon
                sx={{
                  fontSize: '18px',
                }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogFooter>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            gap: 1,
          }}
        >
          <Button
            onClick={handleClose}
            variant='contained'
            disabled={uploadPending}
            data-testid={cancelButtonTestId}
            color='secondary'
          >
            {t('shared:cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!file || uploadPending}
            loading={uploadPending}
            data-testid={submitButtonTestId}
          >
            {importButtonLabel || 'Import file'}
          </Button>
        </Box>
      </DialogFooter>

      {/* Paste Dialog */}
      <Dialog
        open={showPasteDialog}
        onClose={closePasteDialog}
        maxWidth='sm'
        fullWidth
        data-testid={pasteDialogTestId}
      >
        <DialogTitle onClose={closePasteDialog}>
          {t('bulkImport:importCsvFile')}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography
              variant='bodyLarge'
              sx={{
                color: 'text.dark',
              }}
            >
              {t('bulkImport:pasteDescription')}
            </Typography>
            <Box>
              <Typography
                variant='bodyMedium'
                sx={{
                  mb: 1,
                  fontWeight: 500,
                  color: theme.color.onSurface,
                }}
              >
                {t('bulkImport:pasteCsv')}
              </Typography>
              <TextField
                multiline
                rows={6}
                fullWidth
                value={pastedText}
                onChange={handlePastedTextChange}
                placeholder={t('bulkImport:pastePlaceholder')}
                variant='outlined'
                inputProps={{ 'data-testid': pasteDialogTextareaTestId }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.color.surface,
                  },
                }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Link
                onClick={downloadTemplate}
                data-testid={pasteDialogDownloadTemplateTestId}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  textDecoration: 'none',
                  color: theme.color.onSurface,
                }}
              >
                <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                  {t('bulkImport:downloadTemplate')}
                </Typography>
                <DownloadIcon
                  sx={{
                    fontSize: '16px',
                  }}
                />
              </Link>
            </Box>
          </Box>
        </DialogContent>
        <DialogFooter>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              gap: 1,
            }}
          >
            <Button
              onClick={closePasteDialog}
              variant='contained'
              data-testid={pasteDialogCancelTestId}
              color='secondary'
            >
              {t('shared:cancel')}
            </Button>
            <Button
              onClick={handlePasteSubmit}
              disabled={!pastedText.trim()}
              data-testid={pasteDialogImportTestId}
            >
              {t('shared:import')}
            </Button>
          </Box>
        </DialogFooter>
      </Dialog>
    </Dialog>
  );
};

export default ImportCsvModal;
